import { useState } from 'react'
import emailjs from '@emailjs/browser'
import { Alert, Box, Button, Typography, styled } from '@mui/material'

const TextBox = styled('input')<{ $error?: boolean }>(({ theme, $error }) => ({
  border: `1px solid ${$error ? theme.palette.error.main : theme.palette.grey[400]}`,
  borderRadius: '6px',
  height: '24px',
  outline: 'none',
  "&:focus": {
    border: `1px solid ${$error ? theme.palette.error.main : theme.palette.primary.main}`,
    boxShadow: `0 0 3px ${$error ? theme.palette.error.main : theme.palette.primary.main}`
  },
  "&:hover": {
    border: `1px solid ${$error ? theme.palette.error.main : theme.palette.primary.main}`,
    boxShadow: `0 0 3px ${$error ? theme.palette.error.main : theme.palette.primary.main}`
  },
}))

const TextArea = styled('textarea')(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: '6px',
  outline: 'none',
  resize: 'none',
  "&:focus": {
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 3px ${theme.palette.primary.main}`
  },
  "&:hover": {
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 3px ${theme.palette.primary.main}`
  },
}))

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  padding: '40px 24px',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.grey[300]}`,
  gap: '24px',
}))

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [stateMessage, setStateMessage] = useState<string>()
  const [errors, setErrors] = useState({ name: '', email: '', phone: '' })

  const sendEmail = (e: any) => {
    e.persist()
    e.preventDefault()

    const name = e.target.user_name.value.trim()
    const email = e.target.user_email.value.trim()
    const phone = e.target.user_phone.value.trim()

    const newErrors = {
      name: name ? '' : 'Full name is required.',
      email: email ? '' : 'Email is required.',
      phone: phone ? '' : 'Phone number is required.',
    }
    setErrors(newErrors)

    if (newErrors.name || newErrors.email || newErrors.phone) return

    setIsSubmitting(true)
    emailjs
      .sendForm(
        process.env.REACT_APP_SERVICE_ID!,
        process.env.REACT_APP_TEMPLATE_ID!,
        e.target,
        process.env.REACT_APP_PUBLIC_KEY
      )
      .then(
        (result) => {
          setStateMessage('Message sent!');
          setIsSubmitting(false);
          setTimeout(() => {
            setStateMessage(undefined);
          }, 5000); // hide message after 5 seconds
        },
        (error) => {
          setStateMessage('Something went wrong, please try again later');
          setIsSubmitting(false);
          setTimeout(() => {
            setStateMessage(undefined);
          }, 5000); // hide message after 5 seconds
        }
      )

    // Clears the form after sending the email
    e.target.reset()
  }

  return (
    <form onSubmit={sendEmail} style={{ flex: 1 }}>
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            width: '100%'
          }}
        >
          <Typography variant='h6' color='text.primary'>INQUIRY FORM</Typography>
        </Box>
        <Box width="100%" sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography variant='body2' color='text.primary'>FULLNAME</Typography>
          <TextBox
            type="text"
            name="user_name"
            placeholder='Johnathon Doe'
            $error={!!errors.name}
            onChange={() => errors.name && setErrors(prev => ({ ...prev, name: '' }))}
            sx={(theme) => ({
              flex: 1,
              backgroundColor: theme.palette.grey[50],
              padding: '18px 12px',
              fontFamily: '\'Space Grotesk\',sans-serif',
            })}
          />
          {errors.name && <Typography variant='caption' color='error'>{errors.name}</Typography>}
        </Box>
        <Box width="100%" sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography variant='body2' color='text.primary'>EMAIL</Typography>
          <TextBox
            type="email"
            name="user_email"
            placeholder='john@example.com'
            $error={!!errors.email}
            onChange={() => errors.email && setErrors(prev => ({ ...prev, email: '' }))}
            sx={(theme) => ({
              flex: 1,
              backgroundColor: theme.palette.grey[50],
              padding: '18px 12px',
              fontFamily: '\'Space Grotesk\',sans-serif',
            })}
          />
          {errors.email && <Typography variant='caption' color='error'>{errors.email}</Typography>}
        </Box>
        <Box width="100%" sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography variant='body2' color='text.primary'>PHONE</Typography>
          <TextBox
            type="tel"
            name="user_phone"
            placeholder='(123) 456-7890'
            $error={!!errors.phone}
            onChange={() => errors.phone && setErrors(prev => ({ ...prev, phone: '' }))}
            sx={(theme) => ({
              flex: 1,
              backgroundColor: theme.palette.grey[50],
              padding: '18px 12px',
              fontFamily: '\'Space Grotesk\',sans-serif',
            })}
          />
          {errors.phone && <Typography variant='caption' color='error'>{errors.phone}</Typography>}
        </Box>
        <Box width="100%" sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography variant='body2' color='text.primary'>REQUIREMENTS</Typography>
          <TextArea
           name="message" 
           placeholder='Tell us about your pool cleaning needs...'
           rows={8} 
           sx={(theme) => ({
            flex: 1,
            backgroundColor:  theme.palette.grey[50] ,
            padding: '18px 12px',
            fontFamily: '\'Space Grotesk\',sans-serif',
          })} />
        </Box>
        <Button
          type='submit'
          disabled={isSubmitting}
          color="secondary"
          variant='contained'
          sx={{
            marginTop: '6px',
            padding: '18px 12px',
            borderRadius: '8px',
          }}
        >
          <Typography variant='buttonMedium'>Confirm booking request</Typography>
        </Button>
        {stateMessage && (
          <Alert
            severity={stateMessage === 'Message sent!' ? 'success' : 'error'}
            variant="filled"
            sx={{ borderRadius: '8px' }}
          >
            {stateMessage === 'Message sent!'
              ? 'Your message has been sent! We\'ll be in touch shortly.'
              : stateMessage}
          </Alert>
        )}
      </Container>
    </form>
  )
}

export default ContactForm;